'use client';

import React, { createContext, useContext, useState } from 'react';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import ModalFormRepository from '@/creditflowresearch/infrastructure/repository/ModalFormRepository';
import { getContainer } from '@framework/di/getContainer';

interface IMissingSomethingData {
  comments: string,
  email: string,
  recaptcha: string,
}

const useMissingSomethingModal = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isSubmitSucceed, setIsSubmitSucceed] = useState<boolean>(false);
  const container = getContainer();

  const showModal = (): void => setIsVisible(true);
  const hideModal = (): void => setIsVisible(false);

  const resetModal = (): void => {
    setIsVisible(false);
    setIsFetching(false);
    setIsSubmitSucceed(false);
  };

  async function submitForm(file: File, values: IMissingSomethingData) {
    setIsFetching(true);
    try {
      const payload = {
        feedbackText: values.comments,
        email: values.email || '',
        referrer: window.location.href,
        token: values.recaptcha,
        file,
      };
      const modalFormRepository = container.get<ModalFormRepository>(ModalFormRepository);
      await modalFormRepository.sendMissingSomethingModalForm(payload);
      setIsSubmitSucceed(true);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
      setIsSubmitSucceed(false);
    } finally {
      setIsFetching(false);
    }
  }

  return {
    isVisible,
    isFetching,
    isSubmitSucceed,
    showModal,
    hideModal,
    resetModal,
    submitForm,
  };
};

type MissingSomethingModalContextType = ReturnType<typeof useMissingSomethingModal>;

export const MissingSomethingModalContext = createContext<MissingSomethingModalContextType>(null);

export const useMissingSomethingModalContext = (): MissingSomethingModalContextType => {
  const context = useContext<MissingSomethingModalContextType>(MissingSomethingModalContext);
  if (!context) {
    throw new Error('useMissingSomethingModalContext must be used within a MissingSomethingModalContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const MissingSomethingModalProvider = ({ children }: IProps) => (
  <MissingSomethingModalContext.Provider value={ useMissingSomethingModal() }>
    { children }
  </MissingSomethingModalContext.Provider>
);

export default MissingSomethingModalProvider;
